// validateEnquiry.js
//
// Pure, total validation for the Contact form's enquiry payload. This function
// never throws and never mutates its input: it inspects the enquiry and returns
// a verdict plus a per-field error map. The form sends the enquiry only when
// `valid` is true (Requirements 7.3, 7.4).
//
// ContactEnquiry:
//   {
//     name: string,     // required, non-blank
//     email: string,    // required, valid email
//     phone: string,    // required, non-blank
//     subject: string,  // required, non-blank
//     message: string   // required, non-blank
//   }
//
// Result:
//   {
//     valid: boolean,                 // true only when there are no errors
//     errors: Record<field, string>   // one entry per offending field
//   }

// Fields that must be present and non-blank (whitespace-only counts as blank).
const REQUIRED_FIELDS = ['name', 'phone', 'subject', 'message']

// Pragmatic email shape check: non-empty local part, single @, a dot-separated
// domain with a 2+ char TLD, and no whitespace. Deliberately conservative —
// it rejects obviously malformed addresses without trying to be an RFC parser.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const BLANK_MESSAGE = 'This field is required.'
const EMAIL_MESSAGE = 'Enter a valid email address.'

/**
 * Coerce an arbitrary value to a trimmed string. Non-string, non-finite, and
 * structural values (objects/arrays/null/undefined) collapse to '' so they are
 * treated as blank rather than throwing.
 *
 * @param {*} value
 * @returns {string}
 */
function toTrimmedString (value) {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return String(value)
  return ''
}

/**
 * Validate a contact enquiry. Total and pure: tolerates any input shape
 * (including null/undefined or wrong types) and reports every offending field.
 *
 * @param {*} enquiry - expected to be a ContactEnquiry-shaped object
 * @returns {{ valid: boolean, errors: Record<string, string> }}
 */
export function validateEnquiry (enquiry) {
  const source = enquiry && typeof enquiry === 'object' && !Array.isArray(enquiry)
    ? enquiry
    : {}

  const errors = {}

  // Required, non-blank text fields.
  for (const field of REQUIRED_FIELDS) {
    if (toTrimmedString(source[field]) === '') {
      errors[field] = BLANK_MESSAGE
    }
  }

  // Email: required and well-formed. A blank email reports "required";
  // a present-but-malformed email reports the format message.
  const email = toTrimmedString(source.email)
  if (email === '') {
    errors.email = BLANK_MESSAGE
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = EMAIL_MESSAGE
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  }
}

export default validateEnquiry
